import './Menu.css';
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher/LanguageSwitcher';

export default function Menu() {
    const { t } = useTranslation("common");
    const menu = t("menu", { returnObjects: true }) || {};

    const [openDropdown, setOpenDropdown] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const navRef = useRef(null);

    // Detect mobile (same breakpoint as CSS)
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 1410px)');
        const update = () => setIsMobile(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    // onHover open/close
    const handleDropdown = (name) => setOpenDropdown(name);
    const handleClose = () => setOpenDropdown(null);

    // onClick open/close
    const toggleDropdownClick = (name) => setOpenDropdown(prev => prev === name ? null : name);

    const handleLinkClick = () => { setOpenDropdown(null); setMobileMenuOpen(false); };
    const toggleMobileMenu = () => setMobileMenuOpen(prev => !prev);

    useEffect(() => {
        if (!mobileMenuOpen) return;
        const handleClickAway = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) {
                setMobileMenuOpen(false);
                setOpenDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickAway, true);
        document.addEventListener('touchstart', handleClickAway, true);
        return () => {
            document.removeEventListener('mousedown', handleClickAway, true);
            document.removeEventListener('touchstart', handleClickAway, true);
        };
    }, [mobileMenuOpen]);

    const dropdownProps = (name) => {
        if (isMobile) return {};
        return {
            onMouseEnter: () => handleDropdown(name),
            onMouseLeave: handleClose,
            onFocus: () => handleDropdown(name),
            onBlur: handleClose
        };
    };

    const dropdownContentStyle = (name) => ({
        display: openDropdown === name ? 'block' : 'none'
    });

    return (
        <nav className="menu-nav" ref={navRef}>
            <button
                className={`hamburger${mobileMenuOpen ? ' open' : ''}`}
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
                onClick={toggleMobileMenu}
            >
                <span className="hamburger-bar"></span>
                <span className="hamburger-bar"></span>
                <span className="hamburger-bar"></span>
            </button>
            <ul className="menu-list" style={{ display: mobileMenuOpen ? 'flex' : undefined }}>
                <li className="menu-item dropdown" {...dropdownProps('solutions')}>
                    <button
                        type="button"
                        className="menu-trigger"
                        aria-haspopup="true"
                        aria-expanded={openDropdown === 'solutions'}
                        onClick={() =>
                            isMobile ? toggleDropdownClick('solutions') : handleDropdown('solutions')
                        }
                    >
                        {menu.solutions} <span className="arrow">▼</span>
                    </button>
                    <ul className="dropdown-content" style={dropdownContentStyle('solutions')}>
                        <li><Link to="/solutions/kickstarter" onClick={handleLinkClick}>{menu.kickstarter}</Link></li>
                        <li><Link to="/solutions/implementation" onClick={handleLinkClick}>{menu.implementation}</Link></li>
                        <li><Link to="/solutions/consultancy" onClick={handleLinkClick}>{menu.consultancy}</Link></li>
                    </ul>
                </li>

                <li className="menu-item dropdown" {...dropdownProps('expertise')}>
                    <button
                        type="button"
                        className="menu-trigger"
                        aria-haspopup="true"
                        aria-expanded={openDropdown === 'expertise'}
                        onClick={() =>
                            isMobile ? toggleDropdownClick('expertise') : handleDropdown('expertise')
                        }
                    >
                        {menu.expertise} <span className="arrow">▼</span>
                    </button>
                    <ul className="dropdown-content" style={dropdownContentStyle('expertise')}>
                        <li><Link to="/expertise/industrial4-0" onClick={handleLinkClick}>{menu.industrial4}</Link></li>
                        <li><Link to="/expertise/iiot" onClick={handleLinkClick}>{menu.iiot}</Link></li>
                        <li><Link to="/expertise/cyber-security" onClick={handleLinkClick}>{menu.cyberSecurity}</Link></li>
                    </ul>
                </li>

                <li className="menu-item dropdown" {...dropdownProps('usecases')}>
                    <button
                        type="button"
                        className="menu-trigger"
                        aria-haspopup="true"
                        aria-expanded={openDropdown === 'usecases'}
                        onClick={() =>
                            isMobile ? toggleDropdownClick('usecases') : handleDropdown('usecases')
                        }
                    >
                        {menu.useCases} <span className="arrow">▼</span>
                    </button>
                    <ul className="dropdown-content" style={dropdownContentStyle('usecases')}>
                        <li><Link to="/use-cases/digital-factory" onClick={handleLinkClick}>{menu.digitalFactory}</Link></li>
                        <li><Link to="/use-cases/digital-warehousing" onClick={handleLinkClick}>{menu.digitalWarehousing}</Link></li>
                        <li><Link to="/use-cases/paperless-operations" onClick={handleLinkClick}>{menu.paperlessOps}</Link></li>
                        <li><Link to="/use-cases/realtime-machine-integration" onClick={handleLinkClick}>{menu.realTimeInsight}</Link></li>
                    </ul>
                </li>

                <li className="menu-item page-link">
                    <Link to="/articles" onClick={handleLinkClick}>{menu.articles}</Link>
                </li>
                <li className="menu-item page-link">
                    <Link to="/jobs" onClick={handleLinkClick}>{menu.jobs}</Link>
                </li>

                <li className="menu-item dropdown" {...dropdownProps('about')}>
                    <button
                        type="button"
                        className="menu-trigger"
                        aria-haspopup="true"
                        aria-expanded={openDropdown === 'about'}
                        onClick={() =>
                            isMobile ? toggleDropdownClick('about') : handleDropdown('about')
                        }
                    >
                        {menu.about} <span className="arrow">▼</span>
                    </button>
                    <ul className="dropdown-content" style={dropdownContentStyle('about')}>
                        <li><Link to="/about" onClick={handleLinkClick}>{menu.about}</Link></li>
                        <li><Link to="/contact-us" onClick={handleLinkClick}>{menu.contact}</Link></li>
                    </ul>
                </li>

                {mobileMenuOpen && (
                    <li className="menu-item language-switcher-mobile">
                        <LanguageSwitcher />
                    </li>
                )}
            </ul>
            <div className="language-switcher-desktop">
                <LanguageSwitcher />
            </div>
        </nav>
    );
}